// Web equivalent of Location/CoreLocationProvider.swift: device location through the Geolocation API.
//
// Permission: `authorization` starts `unknown` (and `accuracy` `reduced`) and is set by the Permissions API
// (`navigator.permissions.query({ name: 'geolocation' })` and its `change` event). Nothing is decided on `unknown`: no
// prompt, no updates. A `requestAuthorization()` made meanwhile waits and prompts only if the permission turns out to
// be undecided (`prompt`). Where the Permissions API is missing, the permission can only be learnt by asking, so
// `requestAuthorization()` prompts (getCurrentPosition) and the outcome sets it. Updates start only once the
// permission is known to be granted; a PERMISSION_DENIED error from the watch marks it denied and stops.
//
// Configuration per LocationTrackingMode: `enableHighAccuracy` / `maximumAge` from the mode; the mode's distance
// filter is applied in code (a fix is delivered when it moved at least the filter distance from the last delivered
// fix, or when its accuracy improved by at least that much). Compass heading (deviceorientation) runs only in
// `navigation` while heading is requested; iOS needs `requestCompassPermission()` from a user gesture first.
// Background updates cannot be kept by a web page: the flag is recorded only.

import type { TrackPoint } from '../core/models';
import { distance } from '../core/geo';
import { FixBroadcaster, type FixStream, type Unsubscribe } from './fixBroadcaster';
import { LocationFixConverter, type GeolocationPositionLike } from './locationFixConverter';
import type { LocationProvider, SetModeOptions } from './locationProvider';
import {
  authorizationFromPermissionState,
  geolocationOptions,
  headingFilterDegrees,
  isAuthorizationKnown,
  LocationTrackingModes,
  permitsUpdates,
  type LocationAccuracyAuthorization,
  type LocationAuthorization,
  type LocationTrackingMode,
} from './locationTrackingMode';

/** The part of `navigator.geolocation` used. */
export interface GeolocationLike {
  watchPosition(
    success: (position: GeolocationPositionLike) => void,
    error?: (error: { code: number; message?: string }) => void,
    options?: PositionOptions,
  ): number;
  clearWatch(id: number): void;
  getCurrentPosition(
    success: (position: GeolocationPositionLike) => void,
    error?: (error: { code: number; message?: string }) => void,
    options?: PositionOptions,
  ): void;
}

/** The part of `navigator.permissions` used. */
export interface PermissionsLike {
  query(descriptor: { name: 'geolocation' }): Promise<{ state: PermissionState } & Partial<EventTarget>>;
}

export interface BrowserLocationEnvironment {
  geolocation?: GeolocationLike;
  permissions?: PermissionsLike;
  /** Receives `deviceorientation(absolute)` events (the window). */
  orientationTarget?: EventTarget;
}

/** GeolocationPositionError.PERMISSION_DENIED */
const PERMISSION_DENIED = 1;

function defaultEnvironment(): BrowserLocationEnvironment {
  const nav = globalThis.navigator as (Navigator & { permissions?: PermissionsLike }) | undefined;
  return {
    geolocation: nav?.geolocation as GeolocationLike | undefined,
    permissions: nav?.permissions,
    orientationTarget: typeof globalThis.addEventListener === 'function' ? globalThis : undefined,
  };
}

interface CompassEvent extends Event {
  alpha?: number | null;
  absolute?: boolean;
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
}

export class BrowserLocationProvider implements LocationProvider {
  private auth: LocationAuthorization = 'unknown';
  private acc: LocationAccuracyAuthorization = 'reduced';
  private latest?: TrackPoint;
  private compass?: number;
  private currentMode: LocationTrackingMode = 'off';
  private background = false;
  private headingRequested = false;
  /** `requestAuthorization()` was called while the permission was `unknown`: ask once it is known to be undecided. */
  private requestsAuthorizationWhenKnown = false;
  private permissionsUnsupported: boolean;
  private watchID?: number;
  private watchedOptions?: string;
  private compassEventName?: string;
  private disposed = false;

  private readonly env: BrowserLocationEnvironment;
  private readonly broadcaster = new FixBroadcaster<TrackPoint>();
  private readonly stateListeners = new Set<(provider: LocationProvider) => void>();
  private readonly handleCompass = (event: Event) => this.compassReading(event as CompassEvent);

  constructor(env: BrowserLocationEnvironment = defaultEnvironment()) {
    this.env = env;
    this.permissionsUnsupported = !env.permissions;
    if (env.permissions) this.queryPermission(env.permissions);
  }

  // MARK: LocationProvider

  get authorization(): LocationAuthorization {
    return this.auth;
  }
  get accuracy(): LocationAccuracyAuthorization {
    return this.acc;
  }
  get lastFix(): TrackPoint | undefined {
    return this.latest;
  }
  get heading(): number | undefined {
    return this.compass;
  }
  get mode(): LocationTrackingMode {
    return this.currentMode;
  }
  get backgroundUpdatesRequested(): boolean {
    return this.background;
  }
  /** Whether a geolocation watch is running. */
  get isUpdatingLocation(): boolean {
    return this.watchID !== undefined;
  }

  requestAuthorization(): void {
    switch (this.auth) {
      case 'unknown':
        if (this.permissionsUnsupported) {
          // No Permissions API: the only way to learn the permission is to ask (the user asked for location).
          this.prompt();
        } else {
          this.requestsAuthorizationWhenKnown = true;
        }
        break;
      case 'notDetermined':
        this.prompt();
        break;
      default:
        // Granted: nothing to do. Denied/restricted: the user must change it in the browser settings.
        break;
    }
  }

  requestTemporaryFullAccuracy(_purposeKey?: string): void {
    // Precise Location is an iOS app setting; the web has no equivalent request.
  }

  setMode(mode: LocationTrackingMode, options: SetModeOptions = {}): void {
    const background = options.background ?? false;
    const heading = options.heading ?? LocationTrackingModes[mode].usesHeading;
    if (mode === this.currentMode && background === this.background && heading === this.headingRequested) return;
    this.currentMode = mode;
    this.background = background;
    this.headingRequested = heading;
    this.applyConfiguration();
    this.notifyState();
  }

  start(mode: LocationTrackingMode = 'standard'): void {
    this.setMode(mode);
  }

  stop(): void {
    this.setMode('off');
  }

  subscribe(listener: (fix: TrackPoint) => void): Unsubscribe {
    return this.broadcaster.subscribe(listener);
  }

  fixes(): FixStream<TrackPoint> {
    return this.broadcaster.makeStream();
  }

  subscribeState(listener: (provider: LocationProvider) => void): Unsubscribe {
    this.stateListeners.add(listener);
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  dispose(): void {
    this.disposed = true;
    this.stopUpdates();
    this.broadcaster.finishAll();
    this.stateListeners.clear();
  }

  /** iOS Safari only shows compass headings after this permission, requested from a user gesture. */
  static async requestCompassPermission(): Promise<'granted' | 'denied' | 'unsupported'> {
    type OrientationConstructor = { requestPermission?: () => Promise<PermissionState> };
    const ctor = (globalThis as { DeviceOrientationEvent?: OrientationConstructor }).DeviceOrientationEvent;
    if (!ctor?.requestPermission) return 'unsupported';
    try {
      return (await ctor.requestPermission()) === 'granted' ? 'granted' : 'denied';
    } catch {
      return 'denied';
    }
  }

  // MARK: Permission

  private queryPermission(permissions: PermissionsLike): void {
    permissions
      .query({ name: 'geolocation' })
      .then((status) => {
        if (this.disposed) return;
        this.handleAuthorizationChange(authorizationFromPermissionState(status.state));
        status.addEventListener?.('change', () => {
          if (!this.disposed) this.handleAuthorizationChange(authorizationFromPermissionState(status.state));
        });
      })
      .catch(() => {
        // Some browsers reject `geolocation` queries: fall back to asking when the user requests location.
        this.permissionsUnsupported = true;
        if (this.requestsAuthorizationWhenKnown) {
          this.requestsAuthorizationWhenKnown = false;
          this.prompt();
        }
      });
  }

  /** Shows the browser prompt; the outcome sets the permission (and starts updates when granted). */
  private prompt(): void {
    const geolocation = this.env.geolocation;
    if (!geolocation) return;
    geolocation.getCurrentPosition(
      () => {
        if (!this.disposed && !permitsUpdates(this.auth)) this.handleAuthorizationChange('whenInUse');
      },
      (error) => {
        if (!this.disposed && error.code === PERMISSION_DENIED) this.handleAuthorizationChange('denied');
      },
      { enableHighAccuracy: false, maximumAge: Infinity },
    );
  }

  /** A permission reported by the browser (Permissions API, prompt outcome or a watch error). */
  handleAuthorizationChange(value: LocationAuthorization): void {
    const accuracy: LocationAccuracyAuthorization = permitsUpdates(value) ? 'full' : 'reduced';
    if (value === this.auth && accuracy === this.acc) return;
    this.auth = value;
    this.acc = accuracy;
    if (isAuthorizationKnown(value) && this.requestsAuthorizationWhenKnown) {
      this.requestsAuthorizationWhenKnown = false;
      if (value === 'notDetermined') this.prompt();
    }
    this.applyConfiguration();
    this.notifyState();
  }

  // MARK: Configuration

  private applyConfiguration(): void {
    const geolocation = this.env.geolocation;
    if (!LocationTrackingModes[this.currentMode].isActive || !permitsUpdates(this.auth) || !geolocation) {
      this.stopUpdates();
      return;
    }
    const options = geolocationOptions(this.currentMode);
    const key = JSON.stringify([options.enableHighAccuracy, options.maximumAge]);
    if (this.watchID === undefined || this.watchedOptions !== key) {
      if (this.watchID !== undefined) geolocation.clearWatch(this.watchID);
      this.watchedOptions = key;
      this.watchID = geolocation.watchPosition(
        (position) => this.handlePosition(position),
        (error) => this.handleFailure(error.code),
        options,
      );
    }
    if (LocationTrackingModes[this.currentMode].usesHeading && this.headingRequested) this.startCompass();
    else this.stopCompass();
  }

  private stopUpdates(): void {
    if (this.watchID !== undefined) {
      this.env.geolocation?.clearWatch(this.watchID);
      this.watchID = undefined;
      this.watchedOptions = undefined;
    }
    this.stopCompass();
  }

  private handlePosition(position: GeolocationPositionLike): void {
    if (this.watchID === undefined || this.disposed) return;
    const point = LocationFixConverter.fromPosition(position);
    if (!point) return;
    if (!this.passesDistanceFilter(point)) return;
    this.latest = point;
    this.broadcaster.yield(point);
    this.notifyState();
  }

  /** CoreLocation's distanceFilter, in code; an accuracy improvement of at least the filter distance also passes. */
  private passesDistanceFilter(point: TrackPoint): boolean {
    const last = this.latest;
    if (!last) return true;
    const filter = LocationTrackingModes[this.currentMode].distanceFilter;
    if (distance(last.coordinate, point.coordinate) >= filter) return true;
    const before = last.horizontalAccuracy ?? Infinity;
    const now = point.horizontalAccuracy ?? Infinity;
    return before - now >= filter;
  }

  private handleFailure(code: number): void {
    if (code === PERMISSION_DENIED) {
      this.handleAuthorizationChange('denied');
    }
    // POSITION_UNAVAILABLE / TIMEOUT: transient, the watch keeps trying.
  }

  // MARK: Compass

  private startCompass(): void {
    const target = this.env.orientationTarget;
    if (!target || this.compassEventName) return;
    this.compassEventName = 'ondeviceorientationabsolute' in target ? 'deviceorientationabsolute' : 'deviceorientation';
    target.addEventListener(this.compassEventName, this.handleCompass);
  }

  private stopCompass(): void {
    if (this.compassEventName) {
      this.env.orientationTarget?.removeEventListener(this.compassEventName, this.handleCompass);
    }
    this.compassEventName = undefined;
    if (this.compass !== undefined) {
      this.compass = undefined;
      this.notifyState();
    }
  }

  private compassReading(event: CompassEvent): void {
    if (!(LocationTrackingModes[this.currentMode].usesHeading && this.headingRequested)) return;
    let value: number | undefined;
    if (typeof event.webkitCompassHeading === 'number') {
      // iOS: degrees from magnetic north; accuracy -1 means uncalibrated.
      value = LocationFixConverter.headingDegrees(-1, event.webkitCompassHeading, event.webkitCompassAccuracy ?? 0);
    } else if (event.absolute && typeof event.alpha === 'number') {
      value = LocationFixConverter.headingDegrees(-1, (360 - event.alpha) % 360, 0);
    }
    if (value === undefined) {
      if (this.compass === undefined) return;
    } else if (this.compass !== undefined && angularDifference(value, this.compass) < headingFilterDegrees) {
      return;
    }
    this.compass = value;
    this.notifyState();
  }

  private notifyState(): void {
    for (const listener of [...this.stateListeners]) {
      try {
        listener(this);
      } catch (error) {
        console.error('Location state listener failed', error);
      }
    }
  }
}

function angularDifference(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}
